<script setup lang="ts">
import { computed, ref, toRef, watch } from "vue";
import { useFieldErrors } from "@/composables/useFieldErrors";
import { type InputProps, type TextboxProps } from "@phila/phila-ui-core";

/**
 * `placeholder` and `modelValue` are required in TextboxProps, and a default
 * does not make a required prop optional, so they are dropped from the borrowed
 * type and redeclared as optional.
 */
type TextboxBaseProps = Omit<InputProps & TextboxProps, "modelValue" | "placeholder">;

const props = withDefaults(
  defineProps<
    TextboxBaseProps & {
      modelValue?: string | number;
      placeholder?: string;
      rules?: string;
      /**
       * Input mask, e.g. `##:##` for a clock value. Applied here, on the
       * rendered control, because a `v-mask` on this wrapper never reaches
       * the input: the wrapper's root is vee-validate's renderless Field,
       * which renders a fragment, and Vue cannot attach a directive to that.
       */
      mask?: string;
    }
  >(),
  {
    modelValue: "",
    label: "",
    placeholder: "",
    rules: "",
    mask: "",
  },
);
const name = toRef(props, "id");

const control = ref<{ $el?: HTMLElement } | null>(null);
useFieldErrors(control);

/** Everything Textbox should receive; `rules` and `mask` are ours, not the input's. */
const forwarded = computed(() => {
  const { rules: _rules, mask: _mask, ...rest } = props;
  return rest;
});

const emit = defineEmits(["update:modelValue", "input", "blur"]);
const localValue = ref(props.modelValue);

watch(
  () => props.modelValue,
  (val) => {
    if (val !== localValue.value) localValue.value = val;
  },
);
watch(
  () => localValue.value,
  (val) => {
    emit("update:modelValue", val);
  },
);
</script>
<template>
  <Field
    v-slot="{ errorMessage, handleBlur, handleChange }"
    :name="name"
    type="text"
    :rules="rules"
    :model-value="localValue"
    :validate-on-model-update="false"
    :validate-on-change="false"
  >
    <textbox
      ref="control"
      v-mask="mask"
      v-bind="{ ...forwarded, ...$attrs }"
      v-model="localValue"
      :errors="errorMessage"
      @input="handleChange"
      @blur="handleBlur"
    />
  </Field>
</template>
